import { generateQueryString } from '../../../common/base/report/report-util';

export const buildCronParams = ({
  reportConfig,
  criteria,
  criteriaStats,
  selectedDateType,
  assignUsersList,
}) => {
  const p = {};
  if (!reportConfig) return p;
  const f = reportConfig.filters;

  if (f.showDateRange) { p.toDate = criteria.toDate; p.fromDate = criteria.fromDate; }
  if (f.showDate) { p.fromDate = criteria.fromDate; }
  // Guarded the same way as the cc and business branches below: sending an empty
  // fromPaymentDate/toPaymentDate makes the report look like it has a bound it
  // does not have, which is how a to-date-only report ended up grouped by
  // business date.
  if (f.showPaymentDateRange && criteria.fromPaymentDate && selectedDateType === 'payment') {
    p.fromPaymentDate = criteria.fromPaymentDate;
  }
  if (f.showPaymentDateRange && criteria.toPaymentDate && selectedDateType === 'payment') {
    p.toPaymentDate = criteria.toPaymentDate;
  }
  if (f.showcCCDate && criteria.fromCCDate && selectedDateType === 'cc') { p.fromCCDate = criteria.fromCCDate; }
  if (f.showcCCDate && criteria.toCCDate && selectedDateType === 'cc') { p.toCCDate = criteria.toCCDate; }
  if (f.showSystemDate && criteria.fromSystemDate && selectedDateType === 'business') { p.fromSystemDate = criteria.fromSystemDate; }
  if (f.showSystemDate && criteria.toSystemDate && selectedDateType === 'business') { p.toSystemDate = criteria.toSystemDate; }
  if (f.showTaxYear) { p.taxYear = criteria.taxYears; }
  if (f.showTaxYears && criteria.fromTaxYear) { p.fromTaxYear = criteria.fromTaxYear; }
  if (f.showTaxYears && criteria.toTaxYear) { p.toTaxYear = criteria.toTaxYear; }
  if (f.showTaxStatusCodes) { p.taxStatusList = criteria.taxStatusList; }
  if (f.showPaymentMode) { p.paymentModeList = criteria.paymentModeList; }
  if (f.showUnresolved) { p.unresolved = criteria.showUnresolved; }
  if (f.showAssignedUser && (criteria.assignedTo || []).length > 0) { p.assigned = criteria.assignedTo.join(','); }
  // Only when exactly one user is picked: the report prints that user's name in its header, and
  // the backend has no user collection to resolve the login against (users live in the auth
  // service), so send the display name the picker already holds. Not sent for 0 or 2+ selections.
  if (f.showAssignedUser && (criteria.assignedTo || []).length === 1) {
    const [username] = criteria.assignedTo;
    const match = (assignUsersList || []).find((u) => u.username === username);
    p.assignedFullname = (match && match.fullname) || username;
  }
  if (f.showPrintDetails) { p.printDetails = criteria.printDetails; }
  if (f.showPrintOSOnly) { p.isPrintOSOnly = criteria.isPrintOSOnly; }
  if (f.showPrintOSOnly && criteria.isPrintOSOnly) { p.asOfDate = criteria.asOfDate || ''; }
  if (f.showVoidsOnly) { p.printVoids = criteria.printVoids; }
  if (f.showMailOnly) { p.printMail = criteria.printMail; }
  if (f.showExemptMfgOnly) { p.printExemptMfg = criteria.printExemptMfg; }
  if (f.showPrintFullDesc) { p.printFullDesc = criteria.printFullDesc; }
  if (f.showWorkstation) { p.workstation = criteria.wkst.join(','); }
  if (f.showPaymenttype) { p.paymentType = criteria.paymentType; }
  if (f.showPaymenttypeWithoutTaxPayment) { p.paymentType = criteria.paymentTypeWithoutTaxPayment; }
  if (f.showSpecialPaymentType) { p.specialPaymentType = criteria.specialPaymentType; }
  if (f.showTransactionType) { p.transactionType = criteria.transactionType; }
  if (f.showTaxDistricts) { p.taxDistricts = criteria.taxDistricts; }
  if (f.showTaxType) { p.taxTypes = criteria.taxTypes; }
  if (f.showStatusTaxType) { p.taxStatusType = criteria.taxStatusType; }
  if (f.showTaxFees) { p.taxfeeNumbers = criteria.taxFees; }
  if (f.showPaymentType && criteria.cmnPaymentTypes) { p.cmnPaymentTypes = criteria.cmnPaymentTypes.join(','); }
  if (f.showIncludeAdjustment && criteria.includeAdjustment) { p.includeAdjustment = criteria.includeAdjustment; }
  if (f.showIncludeAdjustments && criteria.includeAdjustment) { p.includeAdjustments = criteria.includeAdjustment; }
  if (f.showDownloadCsv && criteria.tocsv) { p.tocsv = criteria.tocsv; }
  if (f.showExcludeWorkstation && criteria.excludedWkst) { p.excludedWkst = criteria.excludedWkst.join(','); }
  if (f.showVoidedDetails && criteria.printVoidDetails) { p.printVoidDetails = criteria.printVoidDetails; }

  p.allWkst = criteriaStats.allWkst;
  p.allTaxDistricts = criteriaStats.allTaxDistricts;
  p.allTaxYear = criteriaStats.alltaxYears;
  p.allTaxTypes = criteriaStats.allTaxTypes;
  p.allTaxStatus = criteriaStats.allTaxStatus;
  p.allPaymentModes = criteriaStats.allPaymentModes;
  p.allSpecialPaymentType = criteriaStats.allSpecialPaymentType;

  return p;
};

export const queryStringCalc = ({
  reportConfig,
  criteria,
  token,
  devSetting,
  criteriaStats,
  selectedDateType,
  assignUsersList,
}) => {
  const paramsObj = buildCronParams({
    reportConfig, criteria, criteriaStats, selectedDateType, assignUsersList,
  });
  // assignedFullname is the only param that can contain a space, and nothing else in this builder
  // encodes. It stays raw in buildCronParams because the cron runner hands that object straight to
  // the report handler as req.query, with no URL to decode.
  const params = Object.entries(paramsObj).map(([k, v]) => (
    k === 'assignedFullname' ? `${k}=${encodeURIComponent(v)}` : `${k}=${v}`
  ));

  if (token) { params.push(`token=${token}`); }
  if (devSetting && devSetting.isTestEnvironment) { params.push('isTestCafe=1'); }

  return generateQueryString(params);
};

export const reportList = () => {
  const list = [
    {
      description: 'Payments Summary',
      config: {
        route: 'payments/summary',
        filters: {
          showDateRange: true,
          showWorkstation: true,
          showTransactionType: true,
          showPaymenttype: false,
          showPrintDetails: true,
          showTaxType: true,
          showIncludeAdjustment: true,
        },
      },
    },
    {
      description: 'Credit Card Summary',
      config: {
        route: 'payments/creditCardSummary',
        filters: {
          showcCCDate: true,
          showSystemDate: true,
          showSpecialPaymentType: true,
          showPrintOSOnly: true,
          showPaymentDateRange: true,
        },
      },
    },
    {
      description: 'Detail Transaction Listing',
      config: {
        route: 'payments/transactionListing',
        filters: {
          showDateRange: true,
          showWorkstation: true,
          showVoidsOnly: true,
          showMailOnly: true,
          showExemptMfgOnly: true,
          showTaxType: true,
        },
      },
    },
    {
      description: 'Monthly Fee',
      config: {
        route: 'payments/monthlyFee',
        filters: {
          showDateRange: true,
          showPrintDetails: true,
          showTaxFees: true,
        },
      },
    },
    {
      description: 'Receipts in Receipt Order ',
      config: {
        route: 'payments/receiptOrder',
        filters: {
          showDateRange: true,
          showTaxYear: true,
          showWorkstation: true,
          showTaxType: true,
          showPaymentType: true,
        },
      },
    },
    {
      description: 'Tax District Summary',
      config: {
        route: 'payments/schoolApportionment',
        filters: {
          showDateRange: true,
          showTaxYear: true,
          showTaxDistricts: true,
          showPrintDetails: true,
          showTaxType: true,
        },
      },
    },
    {
      description: 'Payment List',
      config: {
        route: 'payments/payment-list',
        filters: {
          showDateRange: true,
          showTaxYear: true,
          showTaxDistricts: true,
          showPrintDetails: true,
          showTaxType: true,
          showWorkstation: true,
          showTaxStatusCodes: true,
          showPaymentMode: true,
          showDownloadCsv: true,
        },
      },
    },
    {
      description: 'Payments on Tax Warrants',
      config: {
        route: 'payments/payments-on-tax-warrants',
        filters: {
          showDateRange: true,
          showDownloadCsv: true,
        },
      },
    },
    {
      description: 'Overpayments Report',
      config: {
        route: 'payments/overpayment-summary',
        filters: {
          showTaxDistricts: true,
          showTitle: false,
          showPageBreakdownYears: false,
          showAsOfDate: false,
          showPrintDetails: false,
          showPrintFullDesc: false,
          showTypeOfCollectionSummary: false,
          showUnresolved: true,
          showAssignedUser: true,
          showTaxType: true,
          showTaxYear: true,
          showDateRange: true,

        },
      },
    },
    {
      // Fixed/canned report: always adj-type, unresolved, overpaid 90+ days ago.
      // No user-facing criteria, so filters intentionally leaves every showXxx flag unset.
      description: 'C/E Overpayments Report',
      config: {
        route: 'payments/ce-overpayment-report',
        filters: {},
      },
    },
    {
      description: 'Tax Records with Balance after Tax Credit',
      config: {
        route: 'payments/taxdue-aftercredit-summary',
        filters: {
          showDateRange: true,
          showTaxYears: false,
          showTaxDistricts: false,
          showTitle: false,
          showPageBreakdownYears: false,
          showAsOfDate: false,
          showPrintDetails: false,
          showPrintFullDesc: false,
          showTypeOfCollectionSummary: false,
          showUnresolved: false,
          showAssignedUser: false,
          showTaxType: false,
          showStatusTaxType: true,
        },
      },
    },
    {
      description: 'Cashiers Report',
      config: {
        route: 'payments/cashiers-summary',
        filters: {
          showDateRange: false,
          showDate: true,
          showTaxYears: false,
          showTaxDistricts: false,
          showTitle: false,
          showPageBreakdownYears: false,
          showAsOfDate: false,
          showPrintDetails: false,
          showPrintFullDesc: false,
          showTypeOfCollectionSummary: false,
          showUnresolved: false,
          showAssignedUser: false,
          showTaxType: false,
        },
      },
    },
    {
      description: 'Payment By Mode',
      config: {
        route: 'payments/payment-by-mode',
        filters: {
          showDateRange: true,
          showDate: false,
          showTaxYears: false,
          showTaxDistricts: false,
          showTitle: false,
          showPageBreakdownYears: false,
          showAsOfDate: false,
          showPrintDetails: false,
          showPrintFullDesc: false,
          showTypeOfCollectionSummary: false,
          showUnresolved: false,
          showAssignedUser: false,
          showTaxType: false,
        },
      },
    },
    {
      description: 'Payment Adjustments by Type',
      config: {
        route: 'payments/adjustment-type',
        filters: {
          showDateRange: true,
          showDate: false,
          showTaxYears: false,
          showTaxDistricts: false,
          showTitle: false,
          showPageBreakdownYears: false,
          showAsOfDate: false,
          showPrintDetails: true,
          showPrintFullDesc: false,
          showTypeOfCollectionSummary: false,
          showUnresolved: false,
          showAssignedUser: false,
          showTaxType: false,
          showPaymentType: false,
          showPaymenttypeWithoutTaxPayment: true,
        },
      },
    },
    {
      description: 'Payment By Workstation',
      config: {
        route: 'payments/payment-by-workstation',
        filters: {
          showDateRange: true,
          showDate: false,
          showTaxYears: false,
          showTaxDistricts: false,
          showTitle: false,
          showPageBreakdownYears: false,
          showAsOfDate: false,
          showPrintDetails: false,
          showPrintFullDesc: false,
          showTypeOfCollectionSummary: false,
          showUnresolved: false,
          showAssignedUser: false,
          showTaxType: false,
          showExcludeWorkstation: true,
        },
      },
    },
    {
      description: 'Special Assessment Receipts',
      config: {
        route: 'payments/special-assessement-receipts',
        filters: {
          showDateRange: true,
          showWorkstation: false,
          showTransactionType: false,
          showPaymenttype: false,
          showPrintDetails: true,
          showTaxType: false,
          showIncludeAdjustment: false,
        },
      },
    },
    {
      description: 'Daily Cash Summary',
      config: {
        route: 'payments/daily-cash-summary-report',
        filters: {
          showDateRange: true,
          showWorkstation: true,
          showTransactionType: false,
          showPaymenttype: false,
          showPrintDetails: false,
          showTaxType: false,
          showIncludeAdjustment: false,
        },
      },
    },
    {
      description: 'Monthly Summary of Deposits and Collections',
      config: {
        route: 'payments/monthly-summary-deposits-collections',
        filters: {
          showDateRange: true,
          showWorkstation: true,
          showTransactionType: false,
          showPaymenttype: false,
          showPrintDetails: true,
          showTaxType: false,
          showIncludeAdjustments: true,
        },
      },
    },
    {
      description: 'Payment Audits Report',
      config: {
        route: 'payments/audit-report',
        filters: {
          showDateRange: true,
          showWorkstation: false,
          showTransactionType: false,
          showPaymenttype: false,
          showPrintDetails: false,
          showTaxType: false,
          showIncludeAdjustment: false,
        },
      },
    },
    {
      description: 'Void Report',
      config: {
        route: 'payments/void-report',
        filters: {
          showDateRange: true,
          showWorkstation: false,
          showTransactionType: false,
          showPaymenttype: false,
          showPrintDetails: false,
          showTaxType: false,
          showIncludeAdjustment: false,
          showVoidedDetails: true,
        },
      },
    },
  ];
  return list;
};

export const queryStringCalcForPaymentList = ({
  criteria,
  token,
}) => {
  const params = [];
  if (criteria.fromPaymentDate) {
    params.push(`fromPaymentDate=${criteria.fromPaymentDate}`);
  }

  if (criteria.toPaymentDate) {
    params.push(`toPaymentDate=${criteria.toPaymentDate}`);
  }

  if (criteria.isPrintOSOnly) {
    params.push(`isPrintOSOnly=${criteria.isPrintOSOnly}`);
  }

  if (token) {
    params.push(`token=${token}`);
  }
  return generateQueryString(params);
};

export const queryStringCalcForPrePostErrorReport = ({
  criteria,
  token,
}) => {
  const params = [];

  if (criteria) {
    params.push(`criteria=${encodeURIComponent(JSON.stringify(criteria))}`);
  }

  if (token) {
    params.push(`token=${token}`);
  }

  return generateQueryString(params);
};

export default {};
